import React from "react";
import {
  Activity,
  Stethoscope,
  Sunrise,
  MessageCircle,
  Quote,
  Star,
  Check,
  Phone,
  Printer,
  MapPin,
  Globe,
  Lock,
  ExternalLink,
} from "lucide-react";
import { SectionHead, formatStamp, formatDate } from "./shared";

export const OverviewSection = ({ person }) => {
  if (!person.overview?.length && !person.currentRoom) return null;
  return (
    <section>
      <SectionHead label="Overview" title="What's happening" />
      <div className="rounded-3xl bg-card shadow-card p-6 sm:p-8">
        {person.currentRoom && (
          <div className="inline-flex items-center gap-2 rounded-full bg-primary-soft px-4 py-1.5 text-xs font-medium text-primary mb-4">
            <Activity className="h-3.5 w-3.5" /> Current room: {person.currentRoom}
          </div>
        )}
        <div className="space-y-4">
          {(person.overview || []).map((p, i) => (
            <p key={i} className="text-sm sm:text-base text-foreground/80 leading-relaxed">{p}</p>
          ))}
        </div>
      </div>
    </section>
  );
};

const journeyMeta = [
  { key: "before", title: "Before surgery", Icon: Sunrise },
  { key: "during", title: "During surgery", Icon: Stethoscope },
  { key: "after", title: "After surgery", Icon: Activity },
];

const stageIcons = [Sunrise, Stethoscope, Activity];

export const JourneySection = ({ person }) => {
  const journey = person.journey;
  if (!journey) return null;

  // New flexible format: journey.stages = [{title, items[]}]
  if (journey.stages?.length) {
    return (
      <section>
        <SectionHead label="The journey" title="What to expect" />
        {person.story && (
          <p data-testid="journey-story" className="mb-6 text-base text-foreground/80 leading-relaxed max-w-2xl">
            {person.story}
          </p>
        )}
        <div className="grid md:grid-cols-3 gap-4">
          {journey.stages.map((stage, idx) => {
            const Icon = stageIcons[idx % stageIcons.length];
            return (
              <div key={idx} className="rounded-3xl bg-card shadow-card p-6">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary-soft">
                  <Icon className="h-4 w-4 text-primary" />
                </span>
                <h3 className="mt-3 font-serif text-lg text-foreground">{stage.title}</h3>
                <ul className="mt-3 space-y-2">
                  {(stage.items || []).map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-foreground/80 leading-relaxed">
                      <Check className="h-3.5 w-3.5 text-primary mt-1 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
        {journey.note && (
          <p className="mt-4 text-xs text-muted-foreground italic text-center max-w-xl mx-auto">{journey.note}</p>
        )}
      </section>
    );
  }

  if (!journeyMeta.some((m) => journey[m.key]?.length)) return null;
  return (
    <section>
      <SectionHead label="The journey" title="What to expect" />
      <div className="grid md:grid-cols-3 gap-4">
        {journeyMeta.map(({ key, title, Icon }) =>
          journey[key]?.length ? (
            <div key={key} className="rounded-3xl bg-card shadow-card p-6">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary-soft">
                <Icon className="h-4 w-4 text-primary" />
              </span>
              <h3 className="mt-3 font-serif text-lg text-foreground">{title}</h3>
              <ul className="mt-3 space-y-2">
                {journey[key].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-foreground/80 leading-relaxed">
                    <Check className="h-3.5 w-3.5 text-primary mt-1 shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null
        )}
      </div>
    </section>
  );
};

export const PatientNotesSection = ({ person, patientNotes }) => {
  if (!patientNotes?.length) return null;
  return (
    <section>
      <SectionHead
        label={`Straight from ${person.firstName}`}
        title={`Notes from ${person.firstName}`}
        subtitle="Little messages, thank-yous, and thoughts in her or his own words."
      />
      <div className="space-y-4">
        {patientNotes.map((n) => (
          <div key={n.id} className="rounded-3xl bg-card shadow-card p-6">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-primary-soft px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-primary">
              <Quote className="h-3 w-3" /> From {person.firstName}
            </div>
            <p className="mt-3 font-serif text-lg text-foreground leading-relaxed">{n.body}</p>
            <p className="mt-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{formatDate(n.date)}</p>
          </div>
        ))}
      </div>
    </section>
  );
};

export const PriorityRequestsSection = ({ person, priorityRequests, onClaim }) => {
  if (!priorityRequests?.length) return null;
  return (
    <section>
      <SectionHead
        label={`Asked by ${person.firstName}`}
        title="Priority requests"
        subtitle={`These came directly from ${person.firstName} — let's get them done quickly.`}
      />
      <div className="space-y-3">
        {priorityRequests.map((r) => (
          <div key={r.id} className="rounded-3xl bg-card shadow-card p-5 flex flex-col sm:flex-row sm:items-center gap-3">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary-soft shrink-0">
              <Star className="h-4 w-4 text-primary" />
            </span>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.2em] text-primary">From {person.firstName}</div>
              <p className="mt-1 font-serif text-base text-foreground">{r.text}</p>
            </div>
            {r.claimedBy ? (
              <div className="inline-flex items-center gap-1.5 text-xs text-primary" data-testid={`priority-claimed-${r.id}`}>
                <Check className="h-3.5 w-3.5" /> {r.claimedBy} is on it
              </div>
            ) : (
              <button
                onClick={() => onClaim(r.id)}
                data-testid={`priority-claim-${r.id}`}
                className="self-start sm:self-auto rounded-full bg-gradient-rose text-primary-foreground px-4 py-1.5 text-xs font-medium shadow-soft hover:shadow-glow transition-gentle"
              >
                I'm on it
              </button>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};

export const UpdatesFeed = ({ person, updates }) => {
  if (!updates?.length) return null;
  return (
    <section>
      <SectionHead label="Live feed" title="Updates" subtitle={`Only ${person.firstName}'s family posts updates here.`} />
      <div className="space-y-4">
        {updates.map((u) => (
          <article key={u.id} className="rounded-3xl bg-card shadow-card p-6 transition-gentle hover:shadow-soft">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{formatStamp(u)}</span>
              {u.fromCareTeam && (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary-soft px-2.5 py-0.5 text-[10px] uppercase tracking-[0.15em] text-primary">
                  <Stethoscope className="h-3 w-3" /> From the care team
                </span>
              )}
            </div>
            {u.title && <h3 className="mt-2 font-serif text-lg text-foreground">{u.title}</h3>}
            <p className="mt-2 text-sm text-foreground/80 leading-relaxed">{u.body}</p>
            {u.author && <p className="mt-3 text-xs text-primary">— {u.author}</p>}
          </article>
        ))}
      </div>
    </section>
  );
};

export const CareTeamSection = ({ person }) => {
  const ct = person.careTeam;
  if (!ct?.doctorName) return null;
  return (
    <section>
      <SectionHead label="Care team" title={ct.doctorName} subtitle={ct.role} />
      <div className="rounded-3xl bg-card shadow-card p-6 sm:p-8">
        {ct.department && <p className="text-sm text-foreground/80">{ct.department}</p>}
        <div className="mt-5 grid sm:grid-cols-2 gap-6">
          <div className="space-y-3">
            {ct.phone && (
              <div className="flex items-start gap-2.5">
                <Phone className="h-4 w-4 text-primary mt-0.5" />
                <div>
                  <div className="text-xs text-muted-foreground">Call for appointment</div>
                  <a href={`tel:${ct.phone}`} className="text-sm text-foreground hover:text-primary transition-gentle">{ct.phone}</a>
                </div>
              </div>
            )}
            {ct.fax && (
              <div className="flex items-start gap-2.5">
                <Printer className="h-4 w-4 text-primary mt-0.5" />
                <div>
                  <div className="text-xs text-muted-foreground">Referral fax</div>
                  <div className="text-sm text-foreground">{ct.fax}</div>
                </div>
              </div>
            )}
            {ct.websiteName && (
              <div className="flex items-start gap-2.5">
                <Globe className="h-4 w-4 text-primary mt-0.5" />
                <div>
                  <div className="text-xs text-muted-foreground">Website</div>
                  {ct.websiteUrl ? (
                    <a href={ct.websiteUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-foreground hover:text-primary transition-gentle">
                      {ct.websiteName}
                    </a>
                  ) : (
                    <div className="text-sm text-foreground">{ct.websiteName}</div>
                  )}
                </div>
              </div>
            )}
          </div>
          {ct.officeLines?.length > 0 && (
            <div className="flex items-start gap-2.5">
              <MapPin className="h-4 w-4 text-primary mt-0.5" />
              <div>
                <div className="text-xs text-muted-foreground">Office</div>
                {ct.officeLines.map((l, i) => (
                  <div key={i} className="text-sm text-foreground">{l}</div>
                ))}
                {ct.directionsUrl && (
                  <a
                    href={ct.directionsUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-1 inline-flex items-center gap-1 text-xs text-primary hover:underline"
                  >
                    Get directions <ExternalLink className="h-3 w-3" />
                  </a>
                )}
              </div>
            </div>
          )}
        </div>
        {ct.mychartUrl && (
          <div className="mt-6 rounded-2xl bg-secondary/50 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <Lock className="h-4 w-4 text-primary shrink-0" />
            <p className="flex-1 text-xs text-muted-foreground">
              {person.firstName}'s record lives in MyChart. Login credentials are private to admins.
            </p>
            <a
              href={ct.mychartUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="self-start rounded-full bg-primary-soft text-primary px-4 py-1.5 text-xs font-medium hover:bg-primary hover:text-primary-foreground transition-gentle"
            >
              Open MyChart
            </a>
          </div>
        )}
      </div>
    </section>
  );
};
